import { compact } from '@art-suite/art-core-ts';
import { getLatestProgressStatus, getLatestProgressUpdate } from './ProgressUpdateLib';
import { WorkStatus } from './ScanRoot';
import { ScanGroupOptimized, ScanPartOptimized, ScanRootOptimized } from './ScanRootOptimized';
import { isTaskScoped } from './StandardScanTemplateRoot';

// ********************************************************************************
// Exported Types
// ********************************************************************************
export type WorkStatusSummary = {
  workStatus: WorkStatus;
  count: number;
  completedCount: number;
  inProgressCount: number;
  needsReworkCount: number;
  notStartedCount: number;
};

// ********************************************************************************
// Exported Constants
// ********************************************************************************
/** The canonical presentation order for the four work statuses, shared by every
 *  status selector/progress surface so they list statuses identically. */
export const WORK_STATUS_ORDER: readonly WorkStatus[] = [
  'notStarted',
  'inProgress',
  'needsRework',
  'completed',
];

// ********************************************************************************
// Exported Public Functions
// ********************************************************************************
export const getScanPartWorkStatus = (scanPart: ScanPartOptimized | undefined) => getLatestProgressUpdate(scanPart);

export const isScanPartWorkCompleted = (scanPart: ScanPartOptimized): boolean => getLatestProgressStatus(scanPart) === 'completed';
export const isScanPartWorkInProgress = (scanPart: ScanPartOptimized): boolean => getLatestProgressStatus(scanPart) === 'inProgress';
export const isScanPartWorkNeedsRework = (scanPart: ScanPartOptimized): boolean => getLatestProgressStatus(scanPart) === 'needsRework';
export const isScanPartWorkNotStarted = (scanPart: ScanPartOptimized): boolean => getLatestProgressStatus(scanPart) === 'notStarted';

export const getWorkStatusSummary = (workStatuses: WorkStatus[]): WorkStatusSummary => {
  const count = workStatuses.length;
  let completedCount = 0;
  let inProgressCount = 0;
  let needsReworkCount = 0;
  let notStartedCount = 0;
  for (const workStatus of workStatuses) {
    if (workStatus === 'completed') completedCount++;
    if (workStatus === 'inProgress') inProgressCount++;
    if (workStatus === 'needsRework') needsReworkCount++;
    if (workStatus === 'notStarted') notStartedCount++;
  }
  return {
    workStatus: count === 0
      ? 'notStarted'
      : completedCount === count
      ? 'completed' // ALL done? then completed
      : needsReworkCount > 0
      ? 'needsRework' // ANY rework? then needs rework
      : notStartedCount === count
      ? 'notStarted' // ALL not started? then not started
      : 'inProgress', // else in progress
    count,
    completedCount,
    inProgressCount,
    needsReworkCount,
    notStartedCount,
  };
};

export const getGroupWorkStatuses = (group: ScanGroupOptimized): WorkStatus[] =>
  compact(group.tasks?.filter(isTaskScoped)?.map(task => getLatestProgressStatus(task) ?? 'notStarted'));

export const getGroupWorkStatusSummary = (group: ScanGroupOptimized) => getWorkStatusSummary(getGroupWorkStatuses(group));
export const getScanWorkStatusSummary = (scan: ScanRootOptimized) => getWorkStatusSummary(scan.groups?.flatMap(getGroupWorkStatuses) ?? []);
export const getScanRootWorkStatuses = (scan: ScanRootOptimized): WorkStatus[] => compact(scan.groups?.flatMap(getGroupWorkStatuses) ?? []);

export const getScanRootWorkStatusSummary = (scan: ScanRootOptimized) => getWorkStatusSummary(getScanRootWorkStatuses(scan));

export const isScanRootWorkCompleted = (scan: ScanRootOptimized): boolean => getScanRootWorkStatusSummary(scan).workStatus === 'completed';
export const isScanRootWorkInProgress = (scan: ScanRootOptimized): boolean => getScanRootWorkStatusSummary(scan).workStatus === 'inProgress';
export const isScanRootWorkNeedsRework = (scan: ScanRootOptimized): boolean => getScanRootWorkStatusSummary(scan).workStatus === 'needsRework';
